import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import DashboardLayout from "layout";
import StatCard from "./modules/StatCard";
import Paper from "@mui/material/Paper";
import user from "../../assets/users.svg";
import flycoin from "../../assets/flycoin.svg";
import withdrawals from "../../assets/withdrawals.svg";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { UnfoldMore } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import {
  getAffiliateOverview,
  IAffiliateReferral,
} from "../../services/affiliate";
import { useNavigate } from "react-router-dom";
import { formatNumberShort } from "utils/helpers";

export interface AffiliateUser {
  id: string;
  name: string;
  email: string;
  referrals: number;
  wallet: string;
  username: string;
  // status: "Paid" | "Pending" | "Failed";
}

const sampleAffiliateUsers: AffiliateUser[] = [
  {
    id: "1",
    name: "Innocent kele",
    email: "blah@gmail.com",
    referrals: 4,
    wallet: "NGN 5,000",
    username: "Kele",
    // status: "Paid",
  },
];

const AffiliateMarketing = () => {
  const navigate = useNavigate();
  // React Query (not a mount effect) so revisits within staleTime reuse the
  // cached overview instead of refetching. Mapping lives in `select` so a
  // shape error can't trigger request retries.
  const { data, isLoading: loading } = useQuery({
    queryKey: ["affiliateOverview"],
    queryFn: getAffiliateOverview,
    select: (response) => {
      const records = response?.data?.records;
      const allreferrals = records?.allreferrals || [];
      return {
        overviewData: {
          totalAffiliates: records?.affliateCount || 0,
          activeAffiliates: records?.total_successful_referral || 0,
          totalEarnings: allreferrals.reduce(
            (total, affiliate) => total + affiliate.referral_bonus_balance,
            0,
          ),
          pendingWithdrawals: records?.total_pending_referrals || 0,
        },
        affiliateUsers: allreferrals
          .map((affiliate: IAffiliateReferral) => ({
            id: affiliate._id,
            username: affiliate?.username || "",
            name:
              affiliate.firstName && affiliate?.lastName
                ? `${affiliate?.firstName} ${affiliate?.lastName}`
                : affiliate?.username || affiliate?.email?.split("@")[0],
            email: affiliate?.email,
            referrals: affiliate?.referral_count,
            wallet: `NGN ${affiliate?.referral_bonus_balance.toLocaleString(
              "en-us",
            )}`,
            // status: affiliate.referral_count > 0 ? "Paid" : "Pending",
          }))
          .sort((a, b) => b.referrals - a.referrals),
      };
    },
  });
  const overviewData = data?.overviewData ?? {
    totalAffiliates: 0,
    activeAffiliates: 0,
    totalEarnings: 0,
    pendingWithdrawals: 0,
  };
  const affiliateUsers: AffiliateUser[] =
    data?.affiliateUsers ?? sampleAffiliateUsers;

  const handleUserClick = (username: string) => {
    navigate(`/affiliate-marketing/user/${username}`);
  };

  const statCards = [
    {
      imageSrc: user,
      label: "Total Affiliates",
      value: overviewData.totalAffiliates,
    },
    {
      imageSrc: user,
      label: "Referrals This Month",
      value: overviewData.activeAffiliates,
    },
    {
      imageSrc: flycoin,
      label: "Total Flycoin",
      value: formatNumberShort(overviewData.totalEarnings),
      subLabel: "NGN",
    },
    {
      imageSrc: withdrawals,
      label: "Pending Withdrawals",
      value: overviewData.pendingWithdrawals,
    },
  ];

  return (
    <DashboardLayout nextSideBar>
      <Box sx={{ py: 3, pt: 0 }}>
        <Typography
          fontSize="24px"
          fontWeight="600"
          variant="h5"
          sx={{ mb: 3 }}
        >
          Affiliate System Overview
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              {statCards.map((stat, index) => (
                <StatCard
                  key={index}
                  imageSrc={stat?.imageSrc}
                  label={stat?.label}
                  value={stat?.value}
                  subLabel={stat?.subLabel}
                />
              ))}
            </Box>
            <Box
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: "10px",
                marginTop: "50px",
                shadow: "none",
                border: "none",
              }}
            >
              {/* <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                }}
              >
                 <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, fontSize: "18px" }}
                >
                  All Affiliate Users
                </Typography> 
                <Box sx={{ display: "none", gap: 1, fontSize: "14px" }}>
                  <IconButton
                    sx={{
                      color: "#667185",
                      fontSize: "12px",
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <SearchIcon sx={{ fontSize: "16px" }} /> <span>Search</span>
                  </IconButton>
                  <IconButton
                    sx={{
                      color: "#667185",
                      fontSize: "12px",
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <FilterListIcon sx={{ fontSize: "16px" }} />{" "}
                    <span>Filter</span>
                  </IconButton>
                  <IconButton
                    sx={{
                      color: "#667185",
                      fontSize: "12px",
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <UnfoldMore sx={{ fontSize: "16px" }} /> <span>Sort</span>
                  </IconButton>
                </Box>
              </Box> */}

              {/* Table */}
              <Box>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ bgcolor: "#F0F2F5" }}>
                      <TableRow>
                        <TableCell
                          sx={{
                            color: "#344054",
                            fontWeight: "400",
                            fontSize: { xs: "12px", sm: "14px" },
                            padding: "14px",
                            paddingLeft: "24px",
                          }}
                        >
                          Name
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#344054",
                            fontWeight: "400",
                            fontSize: "12px",
                            padding: "14px",
                            paddingLeft: { xs: "24px", sm: "24px" },
                          }}
                        >
                          Email
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#344054",
                            fontWeight: "400",
                            fontSize: "12px",
                            padding: "14px",
                          }}
                        >
                          Referrals
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#344054",
                            fontWeight: "400",
                            fontSize: "12px",
                            padding: "14px",
                            paddingRight: { xs: "24px", sm: "14px" },
                          }}
                        >
                          Wallet
                        </TableCell>
                        {/* <TableCell
                          sx={{
                            color: "#344054",
                            fontWeight: "400",
                            fontSize: "12px",
                            padding: "14px",
                          }}
                        >
                          Status
                        </TableCell> */}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {affiliateUsers.map((user, index) => (
                        <TableRow
                          key={index}
                          onClick={() => handleUserClick(user.username)}
                          sx={{
                            cursor: "pointer",
                            paddingLeft: { xs: "24px", sm: "14px" },
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                          }}
                        >
                          <TableCell sx={{ color: "#344054" }}>
                            {user.name}
                          </TableCell>
                          <TableCell sx={{ color: "#667185" }}>
                            {user.email}
                          </TableCell>
                          <TableCell sx={{ color: "#667185" }}>
                            {user.referrals}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "#667185",
                              paddingRight: { xs: "24px", sm: "14px" },
                            }}
                          >
                            {user.wallet}
                          </TableCell>
                          {/* <TableCell>
                            <Box
                              sx={{
                                backgroundColor:
                                  user.status === "Paid"
                                    ? "#ECFDF3"
                                    : user.status === "Pending"
                                    ? "#FEF3E7"
                                    : "#FEE4E2",
                                color:
                                  user.status === "Paid"
                                    ? "#379E66"
                                    : user.status === "Pending"
                                    ? "#F9A000"
                                    : "#DA4733",
                                px: 1.5,
                                py: 0.5,
                                borderRadius: "12px",
                                display: "inline-block",
                                fontSize: "12px",
                                fontWeight: 400,
                              }}
                            >
                              {user.status}
                            </Box>
                          </TableCell> */}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default AffiliateMarketing;
